import isObject from 'lodash/isObject';
import size from 'lodash/size';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import classNames from 'classnames';
import Alert from '@spothero/ui/alert';
import Button from '@spothero/ui/button';
import TextButton from '@spothero/ui/text-button';
import EnvironmentUtils from '@spothero/utils/environment';
import Icon from 'common/icon/Icon';
import UserUtils from 'user/user-utils';
import FormatUtils from 'utils/format';
import CheckoutUtils from '../checkout-utils';
import {updateData} from '../checkout-actions';
import CheckoutPanel from '../common/CheckoutPanel';
import BillingFrequency from './BillingFrequency';
import MonthlyReservation from './MonthlyReservation';
import TransientReservation from './TransientReservation';

class ReservationDetails extends Component {
    static propTypes = {
        isFirstView: PropTypes.bool.isRequired,
        user: PropTypes.object.isRequired,
        isMonthly: PropTypes.bool,
        starts: PropTypes.string,
        selectedRate: PropTypes.object,
        hourlyRates: PropTypes.arrayOf(PropTypes.object),
        monthlyRates: PropTypes.arrayOf(PropTypes.object),
        reservationDetailsSkipped: PropTypes.bool.isRequired,
        reservationDetailsEdit: PropTypes.bool.isRequired,
        phoneNumberRequired: PropTypes.bool,
        vehicleSelectPresent: PropTypes.bool,
        vehicleSelectRequired: PropTypes.bool,
        vehicleMake: PropTypes.string,
        vehicleModel: PropTypes.string,
        vehicleYear: PropTypes.string,
        vehicleColor: PropTypes.string,
        isVehicleUnknown: PropTypes.bool,
        licensePlateRequired: PropTypes.bool,
        selectedLicensePlate: PropTypes.string,
        licensePlateState: PropTypes.string,
        contractFullName: PropTypes.string,
        purchaseOnBehalfOfCustomer: PropTypes.bool,
        purchaseForCustomer: PropTypes.bool,
        isRebookingReservation: PropTypes.bool,
        onContinue: PropTypes.func.isRequired,
        onUpdateData: PropTypes.func.isRequired
    };
    state = {
        iDontKnowVehicleSelected: false,
        termsError: false
    };

    constructor(props) {
        super(props);

        this._isMobile = EnvironmentUtils.isMobileDevice();
    }

    _onIDontKnowChanged = status => {
        this.setState({
            iDontKnowVehicleSelected: status
        });
    }

    _onEditClick = () => {
        this.props.onUpdateData({
            reservationDetailsSkipped: false,
            reservationDetailsEdit: true
        });
    }

    _onContinueClick = evt => {
        const {
            isMonthly,
            vehicleSelectRequired,
            phoneNumberRequired,
            onUpdateData,
            onContinue
        } = this.props;
        const reservationDetailsData = this.submit();

        if (reservationDetailsData) {
            if ((!isMonthly && !vehicleSelectRequired && !phoneNumberRequired) && !size(reservationDetailsData)) {
                reservationDetailsData.reservationDetailsSkipped = true;
            }

            reservationDetailsData.reservationDetailsEdit = false;

            onContinue();
            onUpdateData(reservationDetailsData);
        }
    }

    _renderPreview() {
        const {
            user: {
                phoneNumber
            },
            isMonthly,
            starts,
            selectedRate,
            phoneNumberRequired,
            vehicleSelectRequired,
            vehicleMake,
            vehicleModel,
            vehicleYear,
            vehicleColor,
            isVehicleUnknown,
            licensePlateRequired,
            selectedLicensePlate,
            licensePlateState,
            contractFullName
        } = this.props;

        if (!selectedRate) {
            return null;
        }

        const {
            iDontKnowVehicleSelected
        } = this.state;
        const userPhone = (phoneNumber)
            ? <div>{FormatUtils.phone(phoneNumber)}</div>
            : null;
        const {
            title: rateTitle,
            recurrable
        } = selectedRate;
        const {
            startDateFormatted,
            endDateFormatted
        } = (isMonthly)
            ? CheckoutUtils.getMonthlyReservationInfo(selectedRate, starts)
            : {startDateFormatted: null, endDateFormatted: null};
        let plate = null;

        if ((phoneNumberRequired && !userPhone)
            || ((vehicleSelectRequired || isMonthly)
                && (!iDontKnowVehicleSelected && !isVehicleUnknown && (!vehicleMake || !vehicleModel || !vehicleYear)))
            || (licensePlateRequired && !selectedLicensePlate)
            || (selectedRate && isMonthly && (!vehicleColor || !contractFullName || !licensePlateState))) {
            return null;
        }

        if (licensePlateRequired && selectedLicensePlate) {
            if (selectedLicensePlate === 'DONTKNOW' || selectedLicensePlate === 'RENTAL') {
                plate = <Alert type="highlight">Update license plate # before parking.</Alert>;
            } else {
                plate = selectedLicensePlate;
            }
        }

        if (userPhone || plate || vehicleMake || iDontKnowVehicleSelected) {
            const showVehiclePreview = (plate
                || (vehicleMake && (vehicleSelectRequired || isMonthly))
                || iDontKnowVehicleSelected);

            return (
                <Fragment>
                    {isMonthly &&
                        <Fragment>
                            <span className="CheckoutPanel-preview-rate">
                                <h3>Rate Details</h3>
                                {rateTitle}
                            </span>
                            <span className="CheckoutPanel-preview-reservation">
                                <h3>Reservation</h3>
                                {startDateFormatted} - {endDateFormatted}
                            </span>
                            <span className="CheckoutPanel-preview-billing-frequency">
                                <h3>Billing Frequency</h3>
                                <BillingFrequency recurrable={recurrable} />
                            </span>
                        </Fragment>
                    }
                    {showVehiclePreview &&
                        <span className="CheckoutPanel-preview-vehicle">
                            <h3>Vehicle Information</h3>
                            {vehicleMake && (vehicleSelectRequired || isMonthly) &&
                                <div>
                                    {vehicleYear} {vehicleMake.replace(/-/g, ' ')} {vehicleModel.replace(/-/g, ' ')}
                                </div>
                            }
                            {plate}
                            {iDontKnowVehicleSelected &&
                                <Alert type="highlight">This spot may charge fees onsite for oversized vehicles.<br />Enter vehicle details before parking to see estimated fees.</Alert>
                            }
                        </span>
                    }
                    {(userPhone || contractFullName) &&
                        <span className="CheckoutPanel-preview-phone">
                            <h3>Contact Information</h3>
                            {contractFullName}
                            {userPhone}
                        </span>
                    }
                </Fragment>
            );
        } else {
            return null;
        }
    }

    _renderReservationDetailEdit() {
        return (
            <TextButton
                className="CheckoutPanel-title-actions"
                onClick={this._onEditClick}
            >
                Edit
            </TextButton>
        );
    }

    submit() {
        const {
            licensePlateRequired,
            vehicleSelectPresent,
            vehicleSelectRequired,
            phoneNumberRequired,
            isMonthly,
            selectedRate: {
                terms_and_conditions: termsURL
            }
        } = this.props;

        const licensePlateForm = (isMonthly)
            ? this._monthlyReservation.licensePlateForm
            : this._transientReservation.licensePlateForm;
        const licensePlateStateForm = (isMonthly)
            ? this._monthlyReservation.licensePlateStateForm
            : null;
        const vehicleColorSelectForm = (isMonthly)
            ? this._monthlyReservation.vehicleColorSelectForm
            : null;
        const phoneForm = (isMonthly)
            ? this._monthlyReservation.phoneForm
            : this._transientReservation.phoneForm;
        const vehicleSelectForm = (isMonthly)
            ? this._monthlyReservation.vehicleSelectForm
            : this._transientReservation.vehicleSelectForm;
        const contractNameForm = (isMonthly)
            ? this._monthlyReservation.contractNameForm
            : null;
        const licensePlateData = (licensePlateRequired && licensePlateForm) ? licensePlateForm.submit() : true;
        const licensePlateStateData = (isMonthly && licensePlateStateForm) ? licensePlateStateForm.submit() : true;
        const vehicleColorSelectData = (isMonthly && vehicleColorSelectForm) ? vehicleColorSelectForm.submit() : true;
        const phoneData = (phoneForm) ? phoneForm.submit() : true;
        const vehicleSelectData = ((isMonthly || vehicleSelectPresent) && vehicleSelectForm)
            ? vehicleSelectForm.submit()
            : true;
        const contractNameData = (isMonthly && contractNameForm) ? contractNameForm.submit() : true;

        if (phoneData && isObject(phoneData) && phoneData.smsParkingPass) {
            phoneData.smsParkingPass = true;
        }

        if (termsURL) {
            if (!this._monthlyReservation.termsAgreed) {
                this.setState({
                    termsError: true
                });

                return false;
            } else {
                this.setState({
                    termsError: false
                });
            }
        }

        if (licensePlateData &&
            (!phoneNumberRequired || phoneData) &&
            (!isMonthly || licensePlateStateData) &&
            (!isMonthly || vehicleColorSelectData) &&
            (!isMonthly || contractNameData) &&
            ((!vehicleSelectRequired && !isMonthly) || vehicleSelectData)) {
            let allData = {
                ...licensePlateData,
                ...licensePlateStateData,
                ...vehicleColorSelectData,
                ...contractNameData,
                ...phoneData
            };

            if (isObject(vehicleSelectData)) {
                allData = {
                    ...allData,
                    ...vehicleSelectData
                };
            }

            if (termsURL && this._monthlyReservation.termsAgreed) {
                allData.acceptedTerms = true;
            }

            return allData;
        } else {
            return false;
        }
    }

    render() {
        const {
            isFirstView,
            user,
            isMonthly,
            selectedRate,
            hourlyRates,
            monthlyRates,
            reservationDetailsEdit,
            reservationDetailsSkipped,
            vehicleSelectRequired,
            licensePlateRequired,
            purchaseOnBehalfOfCustomer,
            purchaseForCustomer,
            isRebookingReservation
        } = this.props;
        const {
            termsError
        } = this.state;
        const hasMultipleRates = ((selectedRate && (!isMonthly && hourlyRates && hourlyRates.length > 1)) || (isMonthly && monthlyRates && monthlyRates.length > 1));
        const reservationPreview = this._renderPreview();
        const isOpen = (selectedRate && user.status !== UserUtils.AUTH_STATE.LOGGED_OUT && (reservationDetailsEdit || (!reservationPreview && !reservationDetailsSkipped) || (vehicleSelectRequired && isFirstView) || (licensePlateRequired && isFirstView) || (isFirstView && hasMultipleRates))) || false;
        const isCompleted = (reservationPreview || reservationDetailsSkipped);
        const classes = classNames(
            'CheckoutPanel-reservation',
            {'CheckoutPanel-completed': isCompleted}
        );
        const continueBtnClasses = classNames(
            'btn-continue-to-payment',
            {'admin-border-button': (user.isAdmin && !this._isMobile && (purchaseForCustomer || purchaseOnBehalfOfCustomer || isRebookingReservation))}
        );

        return (
            <CheckoutPanel
                className={classes}
                title={(
                    <Fragment>
                        {(isCompleted)
                            ? <em><Icon name="check" /></em>
                            : <em>2</em>
                        } Reservation Details
                    </Fragment>
                )}
                additionalTitleMarkup={(!isOpen && (reservationPreview || reservationDetailsSkipped))
                    ? this._renderReservationDetailEdit()
                    : null}
                open={isOpen}
                preview={reservationPreview}
            >
                {selectedRate &&
                    <div className="ReservationDetails">
                        {(!isMonthly)
                            ? (
                                <TransientReservation
                                    ref={node => { this._transientReservation = node; }}
                                    isOpen={isOpen}
                                    onIDontKnowChanged={this._onIDontKnowChanged}
                                />
                            ) : (
                                <MonthlyReservation
                                    ref={node => { this._monthlyReservation = node; }}
                                    isOpen={isOpen}
                                    termsError={termsError}
                                />
                            )
                        }
                        <div className="FormActions">
                            <Button
                                className={continueBtnClasses}
                                color="primary"
                                onClick={this._onContinueClick}
                            >
                                Continue
                            </Button>
                        </div>
                    </div>
                }
            </CheckoutPanel>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {
            reservationDetailsSkipped,
            reservationDetailsEdit,
            phoneNumberRequired,
            vehicleSelectRequired,
            vehicleSelectPresent,
            vehicleMake,
            vehicleModel,
            vehicleYear,
            vehicleColor,
            isVehicleUnknown,
            licensePlateRequired,
            selectedLicensePlate,
            licensePlateState,
            contractFullName,
            purchaseOnBehalfOfCustomer,
            purchaseForCustomer,
            isRebookingReservation
        },
        searchRequest: {
            monthly,
            starts
        },
        spot: {
            selected: {
                selectedRate,
                houlry_rates: hourlyRates,
                monthly_rates: monthlyRates
            }
        },
        user: {
            data: userData
        }
    } = state;

    return {
        user: userData,
        isMonthly: monthly,
        starts,
        selectedRate,
        hourlyRates,
        monthlyRates,
        reservationDetailsSkipped,
        reservationDetailsEdit,
        phoneNumberRequired,
        vehicleSelectRequired,
        vehicleSelectPresent,
        vehicleMake,
        vehicleModel,
        vehicleYear,
        vehicleColor,
        isVehicleUnknown,
        licensePlateRequired,
        selectedLicensePlate,
        licensePlateState,
        contractFullName,
        purchaseOnBehalfOfCustomer,
        purchaseForCustomer,
        isRebookingReservation
    };
};

const mapDispatchToProps = {
    onUpdateData: updateData
};

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(ReservationDetails);
